<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Chemical Templates</h1>
    <p class="text-muted mb-0">Save and reuse your common spray application recipes</p>
  </div>
  <button class="btn btn-primary" (click)="addTemplate()">
    <i class="bi bi-plus-lg me-2"></i>Create Template
  </button>
</div>

<!-- Info Banner -->
<div class="alert alert-info d-flex align-items-start mb-4 py-2 px-3">
  <i class="bi bi-lightbulb me-2 mt-1"></i>
  <div>
    <span class="small">
      Templates save time by storing your frequently used chemical combinations.
      When creating a spray record, select a template to auto-fill the chemicals and rates.
    </span>
  </div>
</div>

<!-- Templates Grid -->
<div class="row g-4">
  <ng-container *ngIf="!isLoading() && templates().length > 0; else emptyState">
    <div *ngFor="let template of templates()" class="col-12 col-lg-6">
      <div class="card template-card h-100">
        <div class="card-header p-3">
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <h5 class="mb-1">{{ template.name }}</h5>
              <p class="small text-muted mb-0">{{ template.description }}</p>
            </div>
            <div class="dropdown">
              <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                <i class="bi bi-three-dots-vertical"></i>
              </button>
              <ul class="dropdown-menu dropdown-menu-end">
                <li><a class="dropdown-item" (click)="viewTemplate(template)"><i class="bi bi-eye me-2"></i>View Details</a></li>
                <li><a class="dropdown-item" (click)="editTemplate(template)"><i class="bi bi-pencil me-2"></i>Edit</a></li>
                <li><a class="dropdown-item" (click)="duplicateTemplate(template)"><i class="bi bi-copy me-2"></i>Duplicate</a></li>
                <li><hr class="dropdown-divider"></li>
                <li><a class="dropdown-item text-danger" (click)="deleteTemplate(template)"><i class="bi bi-trash me-2"></i>Delete</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-3">
          <!-- Chemicals List -->
          <div class="mb-3">
            <h6 class="small fw-bold text-muted mb-2">Chemicals ({{ template.items?.length || 0 }})</h6>
            <div class="chemical-list">
              <div *ngFor="let chem of template.items || []; let last = last"
                   class="chemical-row py-2"
                   [class.border-bottom]="!last">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="flex-grow-1">
                    <span class="fw-medium">{{ chem.name }}</span>
                    <span class="text-muted small ms-2">{{ chem.purpose }}</span>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge" [ngClass]="getSourceBadgeClass(chem.source)">
                      {{ getSourceLabel(chem.source) }}
                    </span>
                    <span class="badge rate-badge">{{ chem.rate }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Template Details -->
          <div class="row g-2 small">
            <div class="col-6">
              <span class="text-muted d-block">Target Crops</span>
              <span class="fw-medium">{{ template.targetCrops?.join(', ') }}</span>
            </div>
            <div class="col-6">
              <span class="text-muted d-block">Volume</span>
              <span class="fw-medium">{{ template.totalVolume }}</span>
            </div>
            <div class="col-6" *ngIf="template.applicationTiming">
              <span class="text-muted d-block">Timing</span>
              <span class="fw-medium">{{ template.applicationTiming }}</span>
            </div>
            <div class="col-6" *ngIf="template.estimatedCostPerAcre">
              <span class="text-muted d-block">Est. Cost</span>
              <span class="fw-medium text-success">{{ formatCurrency(template.estimatedCostPerAcre) }}/acre</span>
            </div>
          </div>

          <!-- Target Pests -->
          <div *ngIf="template.targetPests && template.targetPests.length > 0" class="mt-3">
            <span class="text-muted small d-block mb-1">Target Pests</span>
            <div class="d-flex flex-wrap gap-1">
              <span *ngFor="let pest of template.targetPests" class="badge pest-badge">
                {{ pest }}
              </span>
            </div>
          </div>

          <!-- Notes -->
          <div *ngIf="template.notes" class="mt-3 pt-3 border-top">
            <span class="small text-muted fst-italic">
              <i class="bi bi-chat-left-text me-1"></i>{{ template.notes }}
            </span>
          </div>
        </div>

        <div class="card-footer p-3 d-flex justify-content-between align-items-center">
          <div class="small text-muted">
            <i class="bi bi-arrow-repeat me-1"></i>Used {{ template.usageCount }} times
          </div>
          <button class="btn btn-primary btn-sm" (click)="useTemplate(template)">
            <i class="bi bi-play-fill me-1"></i>Use Template
          </button>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-template #emptyState>
    <div class="col-12">
      <div class="card">
        <div class="card-body p-5 text-center">
          <div class="empty-state-icon mb-3">
            <i class="bi bi-collection"></i>
          </div>
          <h5>No Templates Yet</h5>
          <p class="text-muted mb-3">Create your first chemical template to streamline your spray record entry.</p>
          <button class="btn btn-primary" (click)="addTemplate()">
            <i class="bi bi-plus-lg me-2"></i>Create First Template
          </button>
        </div>
      </div>
    </div>
  </ng-template>
</div>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>
